/**
 * Floor Plan Importer — upload a drawn plan, trace it, publish it to the 3D map.
 *
 * Deliberately human-in-the-loop. Segmentation gets the geometry roughly right
 * in one pass, but nothing here should silently become lease data: every
 * detected tenancy is reviewable, renameable and deletable before it is
 * published, and the detection thresholds are exposed rather than hidden.
 *
 * The mapping step is built around one fact about mall plans: they ALREADY
 * colour-code their categories. Purple is fashion, orange is food, blue is
 * services. So the fastest way to categorise two hundred shops is to name the
 * colours once, not the shops two hundred times — which is what the colour
 * mapper does. Naming is then a single keyboard loop: type, Enter, next.
 *
 * Steps: Source -> Detect -> Calibrate -> Map -> Amenities -> Publish.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Upload, Scan, Ruler, ListChecks, Rocket, X, Trash2, RotateCcw,
    ChevronRight, ChevronLeft, AlertTriangle, Check, Layers, FileImage, Loader2,
    Wand2, MapPin, Plus, Search, CornerDownLeft, Eye, EyeOff, Route, Box,
    Info, PenLine, SlidersHorizontal, ChevronDown, Combine, ShieldAlert,
    ZoomIn, ZoomOut, Maximize2,
} from 'lucide-react';

import { loadPlanFile, ACCEPTED } from './planSource';
import { segmentPlan, toWorkingImageData, DEFAULT_OPTIONS } from './segment';
import { detectAmenities, AMENITY_TYPES } from './amenities';
import { shapesToRegions, summariseVectorPalette } from './vectorSource';
import { ensureWinding, centroid as polyCentroid, signedArea } from './geometry';
import { claimGround, unionPolygons } from './reshape';
import { regionsToRecords, amenitiesToRecords, savePlan, loadPlan, clearPlan, DEFAULT_CATEGORY } from './importedPlan';
import {
    REGION_KINDS, KIND_PICKER_ORDER, kindOf, isLeasable, isDrawn, buildWalkableGrid,
} from './regionKinds';
import { CATEGORIES, CATEGORY_COLORS, STATUS_COLORS, FLOORS } from '../data/mallData';
import { BRAND_POOL } from '../data/mallSource';
import { formatNumber } from '../../Shared/Formatters';
import { drawAmenitySymbol } from '../amenitySymbols';
import './plan-importer.css';

const STEPS = [
    { id: 'source', label: 'Source', icon: <Upload size={14} /> },
    { id: 'detect', label: 'Detect', icon: <Scan size={14} /> },
    { id: 'calibrate', label: 'Calibrate', icon: <Ruler size={14} /> },
    { id: 'map', label: 'Map stores', icon: <ListChecks size={14} /> },
    { id: 'amenities', label: 'Amenities', icon: <MapPin size={14} /> },
    { id: 'publish', label: 'Publish', icon: <Rocket size={14} /> },
];

const STATUSES = ['Occupied', 'Vacant', 'Coming Soon', 'Under Renovation'];

const SOURCE_LABEL = {
    image: 'Image — traced',
    pdf: 'PDF — rasterised and traced',
    vector: 'Vector drawing — exact shapes',
    model: '3D model — horizontal section',
};

/** Every real brand the sample estate knows, flattened for autocomplete. */
const BRAND_INDEX = (() => {
    const map = new Map();
    Object.entries(BRAND_POOL).forEach(([category, list]) => {
        list.forEach(([name]) => { if (!map.has(name)) map.set(name, category); });
    });
    return map;
})();
const BRAND_NAMES = [...BRAND_INDEX.keys()].sort();

/**
 * How a shape is painted on the review overlay.
 *
 * Leasable shapes take their CATEGORY colour, because that is the thing being
 * decided about them. Everything else takes its KIND colour, because that is.
 */
const regionColor = (r) => (
    isLeasable(r.kind)
        ? (CATEGORY_COLORS[r.category] || '#64748B')
        : kindOf(r.kind).swatch
);

export default function PlanImporter() {
    const navigate = useNavigate();
    const [step, setStep] = useState('source');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const [plan, setPlan] = useState(null);        // { source, width, height, previewUrl }
    const [fileName, setFileName] = useState('');
    const [floorId, setFloorId] = useState('GF');

    const [opts, setOpts] = useState({ ...DEFAULT_OPTIONS });
    const [regions, setRegions] = useState([]);
    const [palette, setPalette] = useState([]);
    const [inside, setInside] = useState(null);
    const [stats, setStats] = useState(null);
    /*
     * Held in state, not read from storage during render.
     *
     * A bare loadPlan() call here was not reactive: clearing the plan emptied
     * localStorage, but nothing told React to look again. The clear handler's
     * other two calls both set values that were already current — setError to
     * null with no error showing, setStep to the step already open — so React
     * bailed out of both and no render happened at all. The banner then sat
     * there advertising a plan that no longer existed until the page was
     * reloaded by hand.
     */
    const [existingPlan, setExistingPlan] = useState(() => loadPlan());
    const [selectedId, setSelectedId] = useState(null);

    const [markers, setMarkers] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [placingMarker, setPlacingMarker] = useState(false);

    /*
     * Stage zoom. `s` is the magnification over fit-to-stage, `tx`/`ty` a pan in
     * canvas pixels. It lives here rather than in CSS alone because the overlay
     * has to agree with the image exactly -- see planRect, which every piece of
     * overlay maths already goes through, so teaching it about the view teaches
     * hit-testing at the same time.
     */
    const [view, setView] = useState({ s: 1, tx: 0, ty: 0 });
    const viewRef = useRef(view);
    viewRef.current = view;
    /*
     * A shape being drawn by hand, as normalised points, plus where the cursor
     * is so the next edge can be rubber-banded.
     *
     * Tracing gets the overwhelming majority of a sheet, and the last few are
     * usually the awkward ones — a unit whose number is printed across its own
     * demising wall, a shape the drawing leaves open. Rather than push the
     * thresholds until those come out and something else breaks, the operator
     * draws them.
     */
    const [drawShape, setDrawShape] = useState(null);
    const [drawHover, setDrawHover] = useState(null);
    /**
     * What the last drawn shape did to the shapes it landed on.
     *
     * Written from inside the setRegions updater, which React may run twice in
     * development, so it goes through a ref rather than a second setState.
     */
    const claimRef = useRef(null);
    const [drawNote, setDrawNote] = useState(null);

    /*
     * Joining shapes the plan drew as several and the lease counts as one.
     *
     * `null` means the tool is off. An ARRAY means it is on -- empty until the
     * operator has picked anything -- which is what lets the plan know to
     * collect clicks instead of selecting with them.
     */
    const [mergeIds, setMergeIds] = useState(null);
    const [mergeNote, setMergeNote] = useState(null);

    const [showCirculation, setShowCirculation] = useState(true);
    const [listFilter, setListFilter] = useState('all');
    const [query, setQuery] = useState('');

    // Calibration: a line drawn on the plan plus its real length.
    const [calLine, setCalLine] = useState(null);   // { x1, y1, x2, y2 } normalised
    const [calMetres, setCalMetres] = useState(60);
    const [drawingCal, setDrawingCal] = useState(false);

    const canvasRef = useRef(null);
    const wrapRef = useRef(null);
    const nameInputRef = useRef(null);

    // ── source ──────────────────────────────────────────────────────────────
    const onFile = useCallback(async (file) => {
        if (!file) return;
        setError(null);
        setBusy('Reading plan…');
        try {
            const loaded = await loadPlanFile(file);
            setPlan(loaded);
            setFileName(file.name);
            setRegions([]);
            setPalette([]);
            setInside(null);
            setMarkers([]);
            setStats(null);
            setCalLine(null);
            setSelectedId(null);
            setSelectedMarker(null);
            setStep('detect');
        } catch (e) {
            setError(e.message || 'That file could not be read.');
        } finally {
            setBusy(null);
        }
    }, []);

    // ── detect ──────────────────────────────────────────────────────────────
    const runDetect = useCallback(async () => {
        if (!plan) return;
        setError(null);
        setBusy('Tracing tenancies…');
        // Yield a frame so the spinner paints before the main thread blocks.
        await new Promise((r) => setTimeout(r, 30));
        try {
            const imageData = toWorkingImageData(plan.source, opts.maxWorkingEdge, opts);

            /*
             * A drawing that already states where its rooms are does not need
             * tracing, and tracing it would be strictly worse: the polygons
             * would come back within a pixel or two of coordinates we were
             * handed exactly. So closed vector shapes go straight through.
             *
             * Linework-only drawings — the common case for an architect's DXF,
             * which has walls but no room boundaries — fall through to the
             * raster path, which floods the space BETWEEN the walls instead.
             */
            const exact = plan.shapes && plan.closedShapes >= 3;
            const result = exact
                ? (() => {
                    const regs = shapesToRegions(plan.shapes);
                    return {
                        regions: regs,
                        palette: summariseVectorPalette(regs),
                        inside: null,
                        stats: {
                            width: plan.width,
                            height: plan.height,
                            totalRegions: plan.shapes.length,
                            kept: regs.length,
                            rejectedSmall: plan.shapes.length - regs.length,
                            byKind: regs.reduce((acc, r) => {
                                acc[r.kind] = (acc[r.kind] || 0) + 1;
                                return acc;
                            }, {}),
                            exact: true,
                        },
                    };
                })()
                : segmentPlan(imageData, opts);
            // Codes are numbered across the LEASABLE shapes only, so unit codes
            // stay a clean run of GF-001.. rather than skipping every atrium.
            let unitNo = 0;
            setRegions(result.regions.map((r) => {
                const leasable = isLeasable(r.kind);
                if (leasable) unitNo += 1;
                return {
                    ...r,
                    name: '',
                    code: leasable ? `${floorId}-${String(unitNo).padStart(3, '0')}` : '',
                    category: DEFAULT_CATEGORY,
                    status: 'Occupied',
                    /*
                     * Whether a category has been CHOSEN, as opposed to sitting
                     * on the default. Distinct from manualCategory, which means
                     * "overridden on this one shape" and exists to stop a bulk
                     * assignment trampling it. Until a category is chosen the
                     * shape renders in the colour the plan drew it in, which is
                     * far more use than a floor of identical grey.
                     */
                    categorised: false,
                    manualCategory: false,
                    manualKind: false,
                    /*
                     * What the trace is unsure about, and whether the operator
                     * has looked. Both live on the shape rather than in a list
                     * beside it, so deleting, joining or redrawing a shape
                     * takes its doubts with it and the review can never point
                     * at something that is no longer there.
                     */
                    flags: r.flags || [],
                    reviewed: false,
                };
            }));
            setPalette(result.palette);
            setInside(result.inside);
            /*
             * The report from the finer raster when there was one, and the
             * trace's own otherwise. The gradient pass runs either way now, so
             * taking only the first would hide the "grainy sheet" notice on
             * exactly the plans most likely to be grainy: the small ones.
             */
            setStats({
                ...result.stats,
                edges: imageData.edgeReport || result.stats.edges || null,
            });
            setSelectedId(null);

            // Amenity markers come off the same raster, so detect them now and
            // the Amenities step opens with candidates already on the plan.
            setMarkers(detectAmenities(imageData, { wallLuminance: opts.wallLuminance }));

            if (!result.regions.length) {
                setError(exact
                    ? 'That drawing had no closed shapes big enough to use.'
                    : 'Nothing was traced. Try raising the wall-ink threshold, or lowering the minimum shape size.');
            }
        } catch (e) {
            setError(e.message || 'Detection failed.');
        } finally {
            setBusy(null);
        }
    }, [plan, opts, floorId]);

    // ── overlay rendering ───────────────────────────────────────────────────

    /**
     * Where the plan actually sits inside the overlay canvas.
     *
     * The <img> uses object-fit:contain, so unless the sheet happens to match
     * the stage aspect it is letterboxed. Mapping normalised coordinates across
     * the WHOLE canvas therefore stretches and offsets every polygon off its
     * shop — which is exactly what it did. All overlay maths goes through here.
     */
    const planRect = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !plan) return null;
        const fit = Math.min(canvas.width / plan.width, canvas.height / plan.height);
        const w = plan.width * fit;
        const h = plan.height * fit;
        // Fitted, centred -- what the <img> would show on its own.
        const fx = (canvas.width - w) / 2;
        const fy = (canvas.height - h) / 2;
        // Then zoom about the fitted box's centre and pan. The <img> carries the
        // matching CSS transform, and because the fitted box is centred in the
        // element, a default-origin `scale` is that same centre.
        const { s: z, tx, ty } = view;
        return {
            x: fx + ((w - (w * z)) / 2) + tx,
            y: fy + ((h - (h * z)) / 2) + ty,
            w: w * z,
            h: h * z,
        };
    }, [plan, view]);

    /** The fitted (un-zoomed) box, which the zoom maths needs as its reference. */
    const fittedRect = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !plan) return null;
        const fit = Math.min(canvas.width / plan.width, canvas.height / plan.height);
        const w = plan.width * fit;
        const h = plan.height * fit;
        return { x: (canvas.width - w) / 2, y: (canvas.height - h) / 2, w, h };
    }, [plan]);

    const MIN_ZOOM = 1;
    const MAX_ZOOM = 12;

    /**
     * Zoom to `next`, keeping the plan point under (px, py) -- canvas pixels --
     * pinned. Anchoring on the pointer is what makes zoom usable for this job:
     * the operator is always zooming in ON something, and a centre-anchored zoom
     * would send it off the edge every time.
     */
    const zoomAt = useCallback((next, px, py) => {
        const F = fittedRect();
        if (!F) return;
        const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
        setView((v) => {
            const cur = {
                x: F.x + ((F.w - (F.w * v.s)) / 2) + v.tx,
                y: F.y + ((F.h - (F.h * v.s)) / 2) + v.ty,
                w: F.w * v.s,
                h: F.h * v.s,
            };
            const nx = (px - cur.x) / cur.w;
            const ny = (py - cur.y) / cur.h;
            const w2 = F.w * z;
            const h2 = F.h * z;
            return {
                s: z,
                tx: px - (nx * w2) - F.x - ((F.w - w2) / 2),
                ty: py - (ny * h2) - F.y - ((F.h - h2) / 2),
            };
        });
    }, [fittedRect]);

    const resetView = useCallback(() => setView({ s: 1, tx: 0, ty: 0 }), []);

    const zoomByStep = useCallback((factor) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        zoomAt(viewRef.current.s * factor, canvas.width / 2, canvas.height / 2);
    }, [zoomAt]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !plan) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const box = planRect();
        if (!box) return;

        const trace = (poly) => {
            ctx.beginPath();
            poly.forEach(([nx, ny], i) => {
                const x = box.x + (nx * box.w);
                const y = box.y + (ny * box.h);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            ctx.closePath();
        };

        // Floor first, so units read on top of it. Non-leasable shapes are
        // washed lightly: they are context, not the thing being decided.
        regions.forEach((r) => {
            if (!isDrawn(r.kind) || isLeasable(r.kind)) return;
            if (!showCirculation) return;
            const c = regionColor(r);
            trace(r.polygon);
            ctx.fillStyle = `${c}22`;
            ctx.fill();
            ctx.strokeStyle = `${c}99`;
            ctx.lineWidth = 1;
            ctx.stroke();
        });

        regions.forEach((r) => {
            if (!isLeasable(r.kind)) return;
            const isSel = r.id === selectedId;
            trace(r.polygon);
            ctx.fillStyle = isSel ? 'rgba(250,204,21,0.6)' : `${regionColor(r)}66`;
            ctx.fill();
            ctx.lineWidth = isSel ? 2.5 : 1.2;
            ctx.strokeStyle = isSel ? '#FACC15' : regionColor(r);
            ctx.stroke();
        });

        /*
         * Ring the doubtful shapes in red.
         *
         * A flag in a list is only useful if you can find what it is talking
         * about, and on a sheet of two hundred shapes reading a code off a row
         * and hunting for it is the part nobody does. Dashed, so it reads as a
         * question rather than as a selection.
         */
        regions.forEach((r) => {
            if (r.reviewed || !(r.flags && r.flags.length)) return;
            trace(r.polygon);
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = r.flags.some((f) => f.severity === 'high') ? '#F43F5E' : '#FB923C';
            ctx.setLineDash([7, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
        });

        // Shapes picked to be joined, in a colour used for nothing else.
        if (mergeIds && mergeIds.length) {
            mergeIds.forEach((id) => {
                const r = regions.find((x) => x.id === id);
                if (!r) return;
                trace(r.polygon);
                ctx.fillStyle = 'rgba(45,212,191,0.45)';
                ctx.fill();
                ctx.lineWidth = 3;
                ctx.strokeStyle = '#2DD4BF';
                ctx.stroke();
            });
        }

        // The selected shape is outlined last whatever its kind, so a corridor
        // or a void can be picked and seen.
        const sel = regions.find((r) => r.id === selectedId);
        if (sel && !isLeasable(sel.kind)) {
            trace(sel.polygon);
            ctx.fillStyle = 'rgba(250,204,21,0.45)';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = '#FACC15';
            ctx.stroke();
        }

        /*
         * Markers carry their PICTOGRAM, the same one the 3D scene draws (see
         * ../amenitySymbols). Reviewing a plan means checking that the thing the
         * detector found is the thing it says it is, and a row of identical dots
         * cannot be checked -- you would have to click each one to see its type.
         * An untyped marker gets the question mark, so what still needs a
         * decision is visible without opening anything.
         */
        markers.forEach((m) => {
            const x = box.x + (m.x * box.w);
            const y = box.y + (m.y * box.h);
            const isSel = m.id === selectedMarker;
            const r = isSel ? 15 : 12;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = m.type ? 'rgba(8,47,73,0.92)' : 'rgba(30,41,59,0.9)';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = isSel ? '#FACC15' : (m.type ? '#38BDF8' : '#94A3B8');
            ctx.stroke();

            const glyph = r * 1.25;
            ctx.save();
            ctx.translate(x - (glyph / 2), y - (glyph / 2));
            ctx.fillStyle = '#F8FAFC';
            ctx.strokeStyle = '#F8FAFC';
            drawAmenitySymbol(ctx, m.type, glyph);
            ctx.restore();
        });

        if (drawShape && drawShape.length) {
            const pts = drawHover ? [...drawShape, drawHover] : drawShape;
            ctx.beginPath();
            pts.forEach(([nx, ny], i) => {
                const x = box.x + (nx * box.w);
                const y = box.y + (ny * box.h);
                if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
            });
            if (drawShape.length >= 2) {
                ctx.closePath();
                ctx.fillStyle = 'rgba(236,72,153,0.22)';
                ctx.fill();
            }
            ctx.strokeStyle = '#EC4899';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.stroke();
            ctx.setLineDash([]);

            drawShape.forEach(([nx, ny], i) => {
                const x = box.x + (nx * box.w);
                const y = box.y + (ny * box.h);
                ctx.beginPath();
                // The first corner is drawn larger: it is the one you click to
                // close the shape, so it has to look like a target.
                ctx.arc(x, y, i === 0 ? 6 : 4, 0, Math.PI * 2);
                ctx.fillStyle = i === 0 ? '#FACC15' : '#EC4899';
                ctx.fill();
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = '#0F172A';
                ctx.stroke();
            });
        }

        if (calLine) {
            ctx.beginPath();
            ctx.moveTo(box.x + (calLine.x1 * box.w), box.y + (calLine.y1 * box.h));
            ctx.lineTo(box.x + (calLine.x2 * box.w), box.y + (calLine.y2 * box.h));
            ctx.strokeStyle = '#22D3EE';
            ctx.lineWidth = 3;
            ctx.setLineDash([8, 5]);
            ctx.stroke();
            ctx.setLineDash([]);
        }
    }, [plan, regions, showCirculation, markers, selectedMarker, selectedId, calLine,
        planRect, drawShape, drawHover, mergeIds]);

    useEffect(() => { draw(); }, [draw]);

    // Keep the overlay canvas pixel-matched to its displayed size.
    useEffect(() => {
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas || !plan) return undefined;
        const resize = () => {
            // The wrapper carries a border; measuring it instead of the canvas
            // leaves the backing store a couple of pixels wider than the CSS box
            // and skews every coordinate slightly.
            const rect = canvas.getBoundingClientRect();
            canvas.width = Math.max(1, Math.round(rect.width));
            canvas.height = Math.max(1, Math.round(rect.height));
            draw();
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [plan, draw]);

    const toNorm = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const box = planRect();
        if (!box) return { x: 0, y: 0 };
        // client px -> canvas px -> plan-relative 0..1
        const px = ((e.clientX - rect.left) / rect.width) * canvas.width;
        const py = ((e.clientY - rect.top) / rect.height) * canvas.height;
        return { x: (px - box.x) / box.w, y: (py - box.y) / box.h };
    };

    const focusName = () => window.setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 0);

    /*
     * The plan's own colour under a point, so a hand-drawn shape joins the same
     * colour group as its neighbours and is categorised with them in one go.
     * The sample raster is small and built once: this is asked a few times per
     * import, and only ever for one pixel.
     */
    const sampleRef = useRef(null);
    const samplePlanColor = useCallback((nx, ny) => {
        if (!plan) return null;
        if (!sampleRef.current) {
            const w = 400;
            const h = Math.max(1, Math.round((w * plan.height) / plan.width));
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            const cx = c.getContext('2d', { willReadFrequently: true });
            cx.drawImage(plan.source, 0, 0, w, h);
            sampleRef.current = cx.getImageData(0, 0, w, h);
        }
        const img = sampleRef.current;
        const x = Math.min(img.width - 1, Math.max(0, Math.round(nx * img.width)));
        const y = Math.min(img.height - 1, Math.max(0, Math.round(ny * img.height)));
        const p = ((y * img.width) + x) * 4;
        const hex = (v) => v.toString(16).padStart(2, '0');
        return `#${hex(img.data[p])}${hex(img.data[p + 1])}${hex(img.data[p + 2])}`;
    }, [plan]);
    useEffect(() => { sampleRef.current = null; }, [plan]);

    /** Turn a hand-drawn outline into a region the rest of the step can use. */
    const commitDrawnShape = useCallback((points) => {
        if (!points || points.length < 3) return;
        const poly = points.map(([x, y]) => [
            Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y)),
        ]);
        const ring = ensureWinding(poly);
        const centre = polyCentroid(ring);
        const colour = samplePlanColor(centre[0], centre[1]) || '#EC4899';
        const id = `manual-${Date.now().toString(36)}`;

        setRegions((rs) => {
            /*
             * The drawn shape TAKES the ground it covers.
             *
             * Nobody draws a unit onto blank paper: they draw it because the
             * trace got that unit wrong, so the ground is already claimed — by
             * the sliver it made of that unit, or by the neighbour it merged
             * into. Leaving both means the two interpenetrate in the model, the
             * traced one paints over the drawn one, and the floor area is
             * counted twice. That reads, entirely reasonably, as "the shape I
             * drew came out small".
             */
            const { regions: cleared, replaced, trimmed } = claimGround(rs, ring);
            claimRef.current = (replaced || trimmed) ? { replaced, trimmed } : null;
            const nextNo = cleared.filter((r) => isLeasable(r.kind)).length + 1;
            return [...cleared, {
                id,
                polygon: ring,
                centroid: centre,
                areaFraction: Math.abs(signedArea(ring)),
                color: colour,
                colorKey: colour,
                coloured: true,
                solidity: 1,
                aspect: 1,
                enclosedShare: 1,
                fillRatio: 1,
                vertices: ring.length,
                kind: 'tenancy',
                kindGuess: 'tenancy',
                manual: true,
                name: '',
                code: `${floorId}-${String(nextNo).padStart(3, '0')}`,
                category: DEFAULT_CATEGORY,
                status: 'Occupied',
                categorised: false,
                manualCategory: false,
                manualKind: true,
            }];
        });
        setDrawShape(null);
        setDrawHover(null);
        setDrawNote(claimRef.current);
        setSelectedId(id);
        focusName();
    }, [samplePlanColor, floorId]);

    const dismissFlags = useCallback((id) => {
        setRegions((rs) => rs.map((r) => {
            if (id != null && r.id !== id) return r;
            if (!r.flags || !r.flags.length || r.reviewed) return r;
            return { ...r, reviewed: true };
        }));
    }, []);

    const toggleMerge = useCallback((id) => {
        setMergeNote(null);
        setMergeIds((cur) => {
            if (!cur) return [id];
            return cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        });
    }, []);

    /**
     * Join the picked shapes into one unit.
     *
     * The largest is the one whose identity survives -- its code, category and
     * kind -- because on a plan drawn with a line through one unit the larger
     * piece is the unit and the smaller is the strip that was cut off it. A
     * name typed on any of them is kept whichever it was on, since typing one
     * is a deliberate act and losing it would be rude.
     *
     * The join answers the doubt that flagged them, so the result is marked
     * reviewed rather than inheriting a warning about a shape that no longer
     * exists.
     */
    const commitMerge = useCallback(() => {
        const picked = regions.filter((r) => mergeIds && mergeIds.includes(r.id));
        if (picked.length < 2) return;

        const { polygon, pieces } = unionPolygons(picked.map((r) => r.polygon));
        if (!polygon) {
            setMergeNote({
                error: pieces > 1
                    ? 'Those shapes do not touch, so joining them would leave a gap in the'
                        + ' middle. Pick shapes that share a wall — or draw the unit by hand.'
                    : 'Those shapes could not be joined.',
            });
            return;
        }

        const primary = picked.reduce((a, b) => (b.areaFraction > a.areaFraction ? b : a));
        const merged = {
            ...primary,
            polygon,
            centroid: polyCentroid(polygon),
            areaFraction: Math.abs(signedArea(polygon)),
            vertices: polygon.length,
            pixelCount: picked.reduce((n, r) => n + (r.pixelCount || 0), 0),
            labelCount: picked.reduce((n, r) => n + (r.labelCount || 0), 0),
            name: picked.map((r) => r.name).find(Boolean) || primary.name,
            flags: [],
            reviewed: true,
            joined: picked.length,
        };

        setRegions((rs) => rs.flatMap((r) => {
            if (r.id === primary.id) return [merged];
            return mergeIds.includes(r.id) ? [] : [r];
        }));
        setMergeIds(null);
        setMergeNote({ joined: picked.length, code: merged.code || kindOf(merged.kind).label });
        setSelectedId(merged.id);
    }, [regions, mergeIds]);

    // Escape leaves the join half-done rather than committing it, which is what
    // Escape means everywhere else in the importer.
    useEffect(() => {
        if (!mergeIds) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { setMergeIds(null); setMergeNote(null); }
            else if (e.key === 'Enter' && mergeIds.length >= 2) { e.preventDefault(); commitMerge(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mergeIds, commitMerge]);

    /*
     * Wheel to zoom, and middle- or right-drag to pan.
     *
     * Bound natively rather than through React's onWheel because React attaches
     * wheel listeners passively, and a passive listener cannot preventDefault --
     * so the page would scroll behind the zoom.
     *
     * Panning deliberately avoids the LEFT button: that one already draws
     * shapes, places markers, picks shapes to merge and selects units, and
     * stealing it would break all four.
     */
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !plan) return undefined;

        const at = (e) => {
            const rect = canvas.getBoundingClientRect();
            return [
                ((e.clientX - rect.left) / rect.width) * canvas.width,
                ((e.clientY - rect.top) / rect.height) * canvas.height,
            ];
        };

        const onWheel = (e) => {
            e.preventDefault();
            const [px, py] = at(e);
            // Exponential, so each notch feels the same at any magnification.
            zoomAt(viewRef.current.s * Math.exp(-e.deltaY * 0.0015), px, py);
        };

        let panning = null;
        const onDown = (e) => {
            if (e.button !== 1 && e.button !== 2) return;
            e.preventDefault();
            const [px, py] = at(e);
            panning = { px, py, tx: viewRef.current.tx, ty: viewRef.current.ty };
            canvas.setPointerCapture(e.pointerId);
        };
        const onMove = (e) => {
            if (!panning) return;
            const [px, py] = at(e);
            setView((v) => ({ ...v, tx: panning.tx + (px - panning.px), ty: panning.ty + (py - panning.py) }));
        };
        const onUp = () => { panning = null; };
        const noMenu = (e) => e.preventDefault();

        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('pointerdown', onDown);
        canvas.addEventListener('pointermove', onMove);
        canvas.addEventListener('pointerup', onUp);
        canvas.addEventListener('pointercancel', onUp);
        canvas.addEventListener('contextmenu', noMenu);
        return () => {
            canvas.removeEventListener('wheel', onWheel);
            canvas.removeEventListener('pointerdown', onDown);
            canvas.removeEventListener('pointermove', onMove);
            canvas.removeEventListener('pointerup', onUp);
            canvas.removeEventListener('pointercancel', onUp);
            canvas.removeEventListener('contextmenu', noMenu);
        };
    }, [plan, zoomAt]);

    // A new sheet starts fitted; carrying the old zoom over would open the next
    // import scrolled to a corner of a plan that no longer exists.
    useEffect(() => { resetView(); }, [plan, resetView]);

    const onCanvasDown = (e) => {
        if (!plan) return;
        // Middle/right belong to the pan handler above.
        if (e.button === 1 || e.button === 2) return;
        const p = toNorm(e);

        if (drawShape) {
            // Clicking the first corner again closes the shape, which is the
            // gesture every drawing tool uses.
            if (drawShape.length >= 3) {
                const [fx, fy] = drawShape[0];
                if (Math.hypot(fx - p.x, fy - p.y) < 0.012) {
                    commitDrawnShape(drawShape);
                    return;
                }
            }
            setDrawShape((pts) => [...pts, [p.x, p.y]]);
            return;
        }

        // While joining, a click on the plan adds a shape to the pile rather
        // than selecting it. Smallest hit wins, as everywhere else.
        if (mergeIds) {
            const under = regions.filter((r) => pointIn(p, r.polygon));
            if (!under.length) return;
            under.sort((a, b) => a.areaFraction - b.areaFraction);
            toggleMerge(under[0].id);
            return;
        }

        if (drawingCal) {
            setCalLine({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
            return;
        }

        if (placingMarker) {
            const id = `am-manual-${markers.length}-${Math.round(p.x * 1e4)}`;
            setMarkers((ms) => [...ms, { id, x: p.x, y: p.y, type: null, name: '', auto: false, confidence: 'placed by hand' }]);
            setSelectedMarker(id);
            setPlacingMarker(false);
            return;
        }

        if (step === 'amenities') {
            // Markers are small; pick the nearest within a generous radius.
            let best = null;
            let bestD = 0.025;
            markers.forEach((m) => {
                const d = Math.hypot(m.x - p.x, m.y - p.y);
                if (d < bestD) { bestD = d; best = m; }
            });
            setSelectedMarker(best ? best.id : null);
            return;
        }

        // Select the smallest region containing the click, so a shop inside a
        // block wins over the block.
        const hits = regions.filter((r) => pointIn(p, r.polygon));
        if (!hits.length) { setSelectedId(null); return; }
        hits.sort((a, b) => a.areaFraction - b.areaFraction);
        setSelectedId(hits[0].id);
        if (step === 'map') focusName();
    };

    const onCanvasMove = (e) => {
        if (drawShape) { const q = toNorm(e); setDrawHover([q.x, q.y]); return; }
        if (!drawingCal || !calLine) return;
        const p = toNorm(e);
        setCalLine((l) => ({ ...l, x2: p.x, y2: p.y }));
    };

    const onCanvasUp = () => { if (drawingCal) setDrawingCal(false); };

    // Enter finishes the shape, Escape abandons it, Backspace takes back the
    // last corner. Bound only while drawing, so nothing else in the importer
    // has to know about these keys.
    useEffect(() => {
        if (!drawShape) return undefined;
        const onKey = (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitDrawnShape(drawShape); }
            else if (e.key === 'Escape') { setDrawShape(null); setDrawHover(null); }
            else if (e.key === 'Backspace') {
                e.preventDefault();
                setDrawShape((pts) => (pts && pts.length ? pts.slice(0, -1) : pts));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawShape, commitDrawnShape]);

    // ── derived ─────────────────────────────────────────────────────────────
    const planWidthM = useMemo(() => {
        // A model is in real units and a DXF is in drawing units, so the sheet's
        // width is already known. Drawing a reference line still overrides it,
        // because a drawing-unit assumption is worth confirming.
        if (!calLine) return plan && plan.knownScale ? plan.knownScale.widthM : null;
        if (!plan) return null;
        const dxN = (calLine.x2 - calLine.x1);
        const dyN = (calLine.y2 - calLine.y1);
        // Work in pixels so the aspect ratio is respected, then scale up.
        const dxPx = dxN * plan.width;
        const dyPx = dyN * plan.height;
        const lenPx = Math.hypot(dxPx, dyPx);
        if (lenPx < 4) return null;
        const metresPerPx = calMetres / lenPx;
        return plan.width * metresPerPx;
    }, [calLine, calMetres, plan]);

    const planHeightM = planWidthM && plan ? (planWidthM * plan.height) / plan.width : null;

    /**
     * The walkable model, rebuilt from the CLASSIFICATION rather than from
     * pixel colour. Mark a shape as corridor and it becomes routable on the
     * spot; mark one as void and routes stop crossing it. That immediacy is the
     * point of classifying by hand instead of guessing from the raster.
     */
    const walkable = useMemo(() => {
        if (!regions.length || !plan) return null;
        return buildWalkableGrid(regions, plan.width / plan.height, opts.routeGrid, { inside });
    }, [regions, plan, opts.routeGrid, inside]);

    /** Shapes drawn as floor on the overlay, so you can see what routes will use. */
    const circulation = useMemo(
        () => regions.filter((r) => isDrawn(r.kind) && !isLeasable(r.kind)),
        [regions],
    );

    const selected = regions.find((r) => r.id === selectedId) || null;

    const updateRegion = useCallback((id, patch) => {
        setRegions((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    }, []);

    const deleteRegion = useCallback((id) => {
        setRegions((rs) => rs.filter((r) => r.id !== id));
        setSelectedId((cur) => (cur === id ? null : cur));
    }, []);

    /** Assign a category to every region sharing a fill colour. The big lever. */
    const setColorCategory = useCallback((hex, category) => {
        setRegions((rs) => rs.map((r) => (
            // A shop the operator categorised by hand outranks the colour rule.
            (r.colorKey === hex && !r.manualCategory) ? { ...r, category, categorised: true } : r
        )));
    }, []);

    /** Assign a kind to every region sharing a fill colour. */
    const setColorKind = useCallback((hex, kind) => {
        setRegions((rs) => rs.map((r) => (
            (r.colorKey === hex && !r.manualKind) ? { ...r, kind } : r
        )));
    }, []);

    /** Set one shape's kind, and remember that a human said so. */
    const setRegionKind = useCallback((id, kind) => {
        setRegions((rs) => rs.map((r) => (r.id === id ? { ...r, kind, manualKind: true } : r)));
    }, []);

    const deleteColorGroup = useCallback((hex) => {
        setRegions((rs) => rs.filter((r) => r.colorKey !== hex));
        setSelectedId(null);
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return regions.filter((r) => {
            if (listFilter === 'units' && !isLeasable(r.kind)) return false;
            if (listFilter === 'unnamed' && (!isLeasable(r.kind) || r.name)) return false;
            if (listFilter === 'other' && isLeasable(r.kind)) return false;
            if (listFilter === 'to check' && (r.reviewed || !(r.flags && r.flags.length))) return false;
            if (!q) return true;
            return (r.name || '').toLowerCase().includes(q)
                || (r.code || '').toLowerCase().includes(q)
                || r.category.toLowerCase().includes(q)
                || kindOf(r.kind).label.toLowerCase().includes(q);
        });
    }, [regions, listFilter, query]);

    /** Commit the current shop and jump to the next unnamed LEASABLE shape. */
    const selectNextUnnamed = useCallback(() => {
        const at = regions.findIndex((r) => r.id === selectedId);
        for (let i = 1; i <= regions.length; i++) {
            const cand = regions[(at + i + regions.length) % regions.length];
            if (cand && isLeasable(cand.kind) && !cand.name) {
                setSelectedId(cand.id);
                window.setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 0);
                return;
            }
        }
        setSelectedId(null);
    }, [regions, selectedId]);

    const leasable = regions.filter((r) => isLeasable(r.kind));
    const namedCount = leasable.filter((r) => r.name).length;
    const categorisedCount = leasable.filter((r) => r.category !== DEFAULT_CATEGORY).length;
    const typedMarkers = markers.filter((m) => m.type);
    const kindCounts = useMemo(() => {
        const out = {};
        regions.forEach((r) => { out[r.kind] = (out[r.kind] || 0) + 1; });
        return out;
    }, [regions]);

    // ── publish ─────────────────────────────────────────────────────────────
    const publish = () => {
        if (!planWidthM || !regions.length) return;
        const floor = FLOORS.find((f) => f.id === floorId) || FLOORS[1];
        const { units, surfaces } = regionsToRecords(
            floor, regions, { widthM: planWidthM, heightM: planHeightM },
        );

        const existing = loadPlan();
        const otherFloors = existing ? existing.floors.filter((f) => f.id !== floor.id) : [];

        const result = savePlan({
            name: fileName || 'Imported plan',
            updatedAt: new Date().toISOString(),
            floors: [
                ...otherFloors,
                {
                    id: floor.id,
                    name: floor.name,
                    short: floor.short,
                    y: floor.y,
                    level: floor.level,
                    widthM: planWidthM,
                    heightM: planHeightM,
                    previewUrl: plan.previewUrl,
                    units,
                    surfaces,
                    amenities: amenitiesToRecords(floor, markers),
                    // The navigable-space model. Packed one cell per bit, so it
                    // costs about 4 KB rather than the 28 KB a JSON array would.
                    walkable,
                },
            ],
        });

        if (!result.ok) { setError(result.error); return; }
        navigate(`${process.env.PUBLIC_URL}/v3/mallmap?source=imported`);
    };

    const stepIndex = STEPS.findIndex((s) => s.id === step);

    return (
        <div className="pi-page">
            <header className="pi-head">
                <div>
                    <h2><Layers size={18} /> Floor Plan Importer</h2>
                    <p>Trace a drawn mall plan into the 3D map. PNG, JPG or PDF.</p>
                </div>
                <button type="button" className="mm-btn" onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/mallmap`)}>
                    <X size={15} /> Back to map
                </button>
            </header>

            <nav className="pi-steps">
                {STEPS.map((s, i) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`pi-step${step === s.id ? ' is-active' : ''}${i < stepIndex ? ' is-done' : ''}`}
                        onClick={() => { if (plan || s.id === 'source') setStep(s.id); }}
                        disabled={!plan && s.id !== 'source'}
                    >
                        <span className="pi-step-icon">{i < stepIndex ? <Check size={13} /> : s.icon}</span>
                        {s.label}
                    </button>
                ))}
            </nav>

            {error && (
                <div className="pi-alert"><AlertTriangle size={15} /><span>{error}</span>
                    <button type="button" onClick={() => setError(null)}><X size={14} /></button>
                </div>
            )}

            <div className="pi-body">
                <section className="pi-stage" ref={wrapRef}>
                    {plan ? (
                        <>
                            {/*
                              * The same transform planRect applies to the overlay.
                              * The fitted box is centred in this element, so the
                              * default 50% 50% origin IS that box's centre and the
                              * two stay in lockstep at every zoom.
                              */}
                            <img
                                src={plan.previewUrl}
                                alt="Floor plan"
                                className="pi-plan-img"
                                style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})` }}
                            />
                            <canvas
                                ref={canvasRef}
                                className={`pi-overlay${drawingCal || placingMarker || drawShape ? ' is-drawing' : ''}`}
                                onPointerDown={onCanvasDown}
                                onPointerMove={onCanvasMove}
                                onPointerUp={onCanvasUp}
                            />
                            {!!circulation.length && (
                                <button
                                    type="button"
                                    className="pi-stage-toggle"
                                    onClick={() => setShowCirculation((v) => !v)}
                                    title="Show the walkable space wayfinding will use"
                                >
                                    {showCirculation ? <Eye size={13} /> : <EyeOff size={13} />} Circulation
                                </button>
                            )}

                            <div className="pi-zoom" role="group" aria-label="Zoom">
                                <button type="button" onClick={() => zoomByStep(1.4)} title="Zoom in" aria-label="Zoom in">
                                    <ZoomIn size={14} />
                                </button>
                                <button type="button" onClick={() => zoomByStep(1 / 1.4)} title="Zoom out" aria-label="Zoom out">
                                    <ZoomOut size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={resetView}
                                    title="Fit the plan to the stage"
                                    aria-label="Fit plan"
                                    disabled={view.s === 1 && view.tx === 0 && view.ty === 0}
                                >
                                    <Maximize2 size={14} />
                                </button>
                                <span className="pi-zoom-level">{Math.round(view.s * 100)}%</span>
                            </div>
                        </>
                    ) : (
                        <DropZone onFile={onFile} />
                    )}
                    {busy && (
                        <div className="pi-busy"><Loader2 size={22} className="pi-spin" /><span>{busy}</span></div>
                    )}
                </section>

                <aside className="pi-panel">
                    {step === 'source' && (
                        <SourceStep
                            fileName={fileName}
                            plan={plan}
                            floorId={floorId}
                            setFloorId={setFloorId}
                            onFile={onFile}
                            existingPlan={existingPlan}
                            onClear={() => {
                                clearPlan();
                                setExistingPlan(null);
                                setError(null);
                                setStep('source');
                            }}
                        />
                    )}

                    {step === 'detect' && (
                        <DetectStep
                            plan={plan}
                            opts={opts}
                            setOpts={setOpts}
                            onRun={runDetect}
                            regions={regions}
                            stats={stats}
                            busy={!!busy}
                            onReview={() => setStep('map')}
                        />
                    )}

                    {step === 'calibrate' && (
                        <CalibrateStep
                            knownScale={plan && plan.knownScale}
                            drawingCal={drawingCal}
                            setDrawingCal={setDrawingCal}
                            calLine={calLine}
                            calMetres={calMetres}
                            setCalMetres={setCalMetres}
                            planWidthM={planWidthM}
                            planHeightM={planHeightM}
                            onClear={() => setCalLine(null)}
                        />
                    )}

                    {step === 'map' && (
                        <MapStep
                            regions={regions}
                            filtered={filtered}
                            palette={palette}
                            selected={selected}
                            nameInputRef={nameInputRef}
                            namedCount={namedCount}
                            categorisedCount={categorisedCount}
                            listFilter={listFilter}
                            setListFilter={setListFilter}
                            query={query}
                            setQuery={setQuery}
                            onSelect={(id) => { setSelectedId(id); focusName(); }}
                            onUpdate={updateRegion}
                            onDelete={deleteRegion}
                            onColorCategory={setColorCategory}
                            onColorKind={setColorKind}
                            onRegionKind={setRegionKind}
                            onDeleteColorGroup={deleteColorGroup}
                            drawShape={drawShape}
                            drawNote={drawNote}
                            onStartDraw={() => { setSelectedId(null); setDrawNote(null); setDrawShape([]); }}
                            onCancelDraw={() => { setDrawShape(null); setDrawHover(null); }}
                            onFinishDraw={() => commitDrawnShape(drawShape)}
                            mergeIds={mergeIds}
                            mergeNote={mergeNote}
                            onStartMerge={() => {
                                setDrawShape(null);
                                setDrawHover(null);
                                setMergeNote(null);
                                setMergeIds(selectedId ? [selectedId] : []);
                            }}
                            onCancelMerge={() => { setMergeIds(null); setMergeNote(null); }}
                            onToggleMerge={toggleMerge}
                            onCommitMerge={commitMerge}
                            onDismissFlags={dismissFlags}
                            kindCounts={kindCounts}
                            onNext={selectNextUnnamed}
                            planWidthM={planWidthM}
                            planHeightM={planHeightM}
                        />
                    )}

                    {step === 'amenities' && (
                        <AmenityStep
                            markers={markers}
                            setMarkers={setMarkers}
                            selectedMarker={selectedMarker}
                            setSelectedMarker={setSelectedMarker}
                            placing={placingMarker}
                            setPlacing={setPlacingMarker}
                            walkable={walkable}
                            circulation={circulation}
                        />
                    )}

                    {step === 'publish' && (
                        <PublishStep
                            regions={regions}
                            markers={typedMarkers}
                            walkable={walkable}
                            surfaceCount={regions.length - leasable.length}
                            unitCount={leasable.length}
                            planWidthM={planWidthM}
                            planHeightM={planHeightM}
                            floorId={floorId}
                            namedCount={namedCount}
                            onPublish={publish}
                        />
                    )}

                    <footer className="pi-nav">
                        <button
                            type="button"
                            className="mm-btn"
                            disabled={stepIndex <= 0}
                            onClick={() => setStep(STEPS[Math.max(0, stepIndex - 1)].id)}
                        >
                            <ChevronLeft size={15} /> Back
                        </button>
                        <button
                            type="button"
                            className="mm-btn is-primary"
                            disabled={!plan || stepIndex >= STEPS.length - 1}
                            onClick={() => setStep(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)].id)}
                        >
                            Next <ChevronRight size={15} />
                        </button>
                    </footer>
                </aside>
            </div>
        </div>
    );
}

function pointIn(pt, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i];
        const [xj, yj] = poly[j];
        if (((yi > pt.y) !== (yj > pt.y))
            && (pt.x < ((xj - xi) * (pt.y - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
    }
    return inside;
}

function DropZone({ onFile }) {
    const [over, setOver] = useState(false);
    return (
        <label
            className={`pi-drop${over ? ' is-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files[0]); }}
        >
            <FileImage size={34} />
            <strong>Drop a floor plan here</strong>
            <span>or click to browse — PNG, JPG, WebP or PDF</span>
            <input type="file" accept={ACCEPTED} onChange={(e) => onFile(e.target.files[0])} hidden />
        </label>
    );
}

function SourceStep({ fileName, plan, floorId, setFloorId, onFile, existingPlan, onClear }) {
    return (
        <div className="pi-sect">
            <h4>1 · Source plan</h4>
            <p className="pi-hint">
                One plan per level. Import each floor separately and assign it below;
                they stack into the same 3D model, and lifts drawn in the same place
                on two levels are linked automatically.
            </p>

            <ul className="pi-formats">
                <li><FileImage size={12} /> <strong>Image or PDF</strong> — traced from the drawing.</li>
                <li><Layers size={12} /> <strong>DXF, SVG, GeoJSON</strong> — exact shapes, no tracing error. Layers become the colour groups.</li>
                <li><Box size={12} /> <strong>GLB, glTF, OBJ</strong> — cut as a section and traced. Scale comes from the model.</li>
            </ul>

            <label className="pi-field">
                <span>Assign to floor</span>
                <select value={floorId} onChange={(e) => setFloorId(e.target.value)}>
                    {FLOORS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
            </label>

            <label className="pi-file-btn">
                <Upload size={15} /> {plan ? 'Replace plan' : 'Choose file'}
                <input type="file" accept={ACCEPTED} onChange={(e) => onFile(e.target.files[0])} hidden />
            </label>

            {plan && (
                <div className="pi-meta">
                    <div><span>File</span><strong>{fileName}</strong></div>
                    <div><span>Source</span><strong>{SOURCE_LABEL[plan.kind] || plan.kind}</strong></div>
                    <div><span>Raster</span><strong>{plan.width} × {plan.height} px</strong></div>
                    {plan.closedShapes != null && (
                        <div><span>Closed shapes</span><strong>{plan.closedShapes}</strong></div>
                    )}
                    {plan.model && (
                        <div><span>Section</span><strong>{formatNumber(plan.model.segments)} edges</strong></div>
                    )}
                    {plan.knownScale && (
                        <div><span>Scale from file</span><strong>{formatNumber(plan.knownScale.widthM)} wide</strong></div>
                    )}
                </div>
            )}

            {existingPlan && (
                <div className="pi-existing">
                    <strong>Already imported</strong>
                    <span>{existingPlan.floors.map((f) => `${f.short} (${(f.units || []).length})`).join(' · ')}</span>
                    <button type="button" className="mm-btn is-ghost" onClick={onClear}>
                        <Trash2 size={13} /> Clear imported plan
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * A control explains itself on request.
 *
 * The importer has a lot of settings and most of them name something from the
 * drawing rather than something from the app — "enclosure", "solidity",
 * "demising walls". A one-line hint under a slider has to be short enough to
 * skim, which leaves no room to say what the setting is FOR or what to do when
 * the result looks wrong. So the short hint stays and the long answer moves in
 * here, a click away, where it can afford to be a proper explanation.
 */
function Help({ title, children }) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const popRef = useRef(null);
    const [pos, setPos] = useState(null);

    /*
     * Positioned against the VIEWPORT rather than against the button.
     *
     * The panel these live in scrolls, so an absolutely positioned popover is
     * clipped at the panel's edge — and the settings that need explaining most
     * are the ones furthest down, where there is least room left. The first
     * version opened downwards into that gap and the last two settings on the
     * step could not read their own help at all.
     *
     * Fixed positioning alone is not enough, though, and this is the part that
     * bites: the panel is glass, and `backdrop-filter` makes an element a
     * containing block for fixed-position descendants. Inside it, `fixed`
     * quietly behaves like `absolute` — the popover was being placed at x=2154
     * in a 1440-wide window and clipped exactly as before. So it is rendered
     * through a portal on <body>, where fixed means what it says.
     */
    useLayoutEffect(() => {
        if (!open) return undefined;

        const place = () => {
            const btn = btnRef.current;
            const pop = popRef.current;
            if (!btn || !pop) return;
            const b = btn.getBoundingClientRect();
            const w = pop.offsetWidth;
            const h = pop.offsetHeight;
            const margin = 10;

            // Right-aligned to the button so it reads as belonging to it, then
            // pulled back inside the viewport if that would overhang.
            let left = b.right - w;
            left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));

            // Below by preference; above when below would not fit; and if
            // neither fits, as low as it can go without leaving the screen.
            let top = b.bottom + 8;
            if (top + h > window.innerHeight - margin) {
                const above = b.top - 8 - h;
                top = above >= margin ? above : Math.max(margin, window.innerHeight - h - margin);
            }
            setPos({ top, left });
        };

        place();
        // `true` so this also follows scrolling of the panel itself, not just
        // of the window.
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open]);

    // Escape, or a click anywhere else, dismisses it. Without this a popover
    // opened near the bottom can be left stranded over the next thing you click.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onDown = (e) => {
            if (btnRef.current && btnRef.current.contains(e.target)) return;
            if (popRef.current && popRef.current.contains(e.target)) return;
            setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        window.addEventListener('pointerdown', onDown, true);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('pointerdown', onDown, true);
        };
    }, [open]);

    useEffect(() => { if (!open) setPos(null); }, [open]);

    return (
        <span className="pi-help">
            <button
                ref={btnRef}
                type="button"
                className={`pi-help-btn${open ? ' is-open' : ''}`}
                aria-expanded={open}
                aria-label={open ? `Hide help for ${title}` : `What does ${title} mean?`}
                onClick={() => setOpen((v) => !v)}
            >
                <Info size={13} />
            </button>
            {open && createPortal(
                <span
                    ref={popRef}
                    className="pi-help-pop"
                    role="note"
                    // Hidden for the first frame only, while it is measured:
                    // placing it needs its height, and its height needs it
                    // rendered. Rendering it offscreen instead would be a
                    // flash of a popover in the wrong place.
                    style={pos
                        ? { top: `${pos.top}px`, left: `${pos.left}px` }
                        : { top: 0, left: 0, visibility: 'hidden' }}
                >
                    <strong>{title}</strong>
                    {children}
                    <button type="button" className="pi-help-close" onClick={() => setOpen(false)}>
                        Got it
                    </button>
                </span>,
                document.body,
            )}
        </span>
    );
}

/** A group of settings that stays out of the way until it is wanted. */
function Advanced({ label, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`pi-advanced${open ? ' is-open' : ''}`}>
            <button type="button" className="pi-advanced-head" onClick={() => setOpen((v) => !v)}>
                <SlidersHorizontal size={13} />
                <span>{label}</span>
                <ChevronDown size={14} className="pi-advanced-chev" />
            </button>
            {open && <div className="pi-advanced-body">{children}</div>}
        </div>
    );
}

function DetectStep({ opts, setOpts, onRun, regions, stats, busy, plan, onReview }) {
    const set = (patch) => setOpts({ ...opts, ...patch });
    // Counted from the shapes rather than from the trace's own tally, so coming
    // back to this step after fixing some of them shows what is actually left.
    const doubtful = regions.filter((r) => !r.reviewed && r.flags && r.flags.length).length;
    const exact = !!(plan && plan.shapes && plan.closedShapes >= 3);
    return (
        <div className="pi-sect">
            <h4>
                2 · {exact ? 'Read the shapes' : 'Trace the plan'}
                <Help title="Tracing">
                    <p>
                        The plan is read as a drawing: dark lines are walls, and every
                        area they enclose becomes a shape — shops, the mall, atria,
                        cores, back-of-house, the ground outside. Nothing is thrown
                        away.
                    </p>
                    <p>
                        Each shape is then <em>proposed</em> as a kind. Those are
                        guesses from colour, size and position, and you correct them in
                        the next step. Anything you set by hand is never overwritten by
                        a later re-trace.
                    </p>
                    <p>
                        If a few shapes are wrong it is quicker to fix them in step 4
                        than to hunt for a setting here.
                    </p>
                </Help>
            </h4>
            <p className="pi-hint">
                {exact
                    ? `This drawing already states where its rooms are — ${plan.closedShapes} closed
                       shapes — so they are read straight off it. No tracing, no
                       thresholds, and the outlines are exactly the drawing's own.`
                    : `Dark lines are read as walls and every enclosed area becomes a
                       shape — shops, malls, atria, cores, back-of-house, all of it.
                       Nothing is discarded.`}
                {' '}Each shape is proposed as a kind, which you correct in the next
                step, and the kind decides what is leasable and what is walkable.
            </p>

            <button type="button" className="mm-btn is-primary pi-run" onClick={onRun} disabled={busy}>
                <Scan size={15} /> {regions.length ? 'Read again' : (exact ? 'Read shapes' : 'Trace plan')}
            </button>

            {!exact && (
            <Advanced label="Tracing settings">
            <label className="pi-check">
                <input type="checkbox" checked={opts.colouredOnly}
                    onChange={(e) => set({ colouredOnly: e.target.checked })} />
                <span>Use fill colour to guess what each shape is</span>
                <Help title="Use fill colour">
                    <p>
                        Most mall plans wash their shops by category and leave the mall
                        floor pale. With this on, a washed shape is proposed as a shop
                        and a pale one as floor.
                    </p>
                    <p>
                        Turn it off for a line-only drawing with no fills, where every
                        enclosed room should be proposed as a unit instead.
                    </p>
                </Help>
            </label>
            <em className="pi-note-sm">
                Leave this on for a colour-coded plan: washed areas are proposed as
                shops, paper as mall floor. Turn it off for a line-only drawing,
                where every enclosed room is proposed as a unit instead.
            </em>

            <label className="pi-field">
                <span>
                    Demising walls — {opts.edgeSensitivity === 0
                        ? 'off'
                        : `contrast ${opts.edgeSensitivity}`}
                    {stats && stats.edges && stats.edges.noise > 0
                        && ` (grainy sheet — using ${Math.round(stats.edges.threshold)})`}
                </span>
                <input type="range" min="0" max="40" value={opts.edgeSensitivity}
                    onChange={(e) => set({ edgeSensitivity: Number(e.target.value) })} />
                <em>
                    Found on the full-resolution sheet, so a hairline between two shops
                    of the same colour still separates them. Lower if neighbours came
                    back merged into one shape; raise if one shop came back as slivers.
                </em>
                <Help title="Demising walls">
                    <p>
                        A demising wall is the line between two shops. Plans draw it as
                        a hairline, often no darker than the fill either side, and if it
                        is missed the two shops are traced as one.
                    </p>
                    <p>
                        The number is how big a step in brightness counts as a line. A
                        grainy or scanned plan raises it automatically — the panel says
                        so when it does.
                    </p>
                    <p>
                        <strong>Two shops came back as one:</strong> lower it.{' '}
                        <strong>One shop came back as several slivers:</strong> raise it.
                    </p>
                </Help>
            </label>

            <label className="pi-field">
                <span>Wall ink threshold — {opts.wallLuminance}</span>
                <input type="range" min="40" max="200" value={opts.wallLuminance}
                    onChange={(e) => set({ wallLuminance: Number(e.target.value) })} />
                <em>Higher catches faint or anti-aliased linework.</em>
            </label>

            <label className="pi-field">
                <span>Colour tolerance — {opts.colorTolerance}</span>
                <input type="range" min="10" max="70" value={opts.colorTolerance}
                    onChange={(e) => set({ colorTolerance: Number(e.target.value) })} />
                <em>Higher merges similar washes; lower keeps categories apart.</em>
            </label>

            <label className="pi-field">
                <span>Smallest shape — {(opts.minAreaFraction * 100).toFixed(3)}% of sheet</span>
                <input type="range" min="1" max="60" value={Math.round(opts.minAreaFraction * 10000)}
                    onChange={(e) => set({ minAreaFraction: Number(e.target.value) / 10000 })} />
                <em>Anything smaller is absorbed into the shape around it.</em>
                <Help title="Smallest shape">
                    <p>
                        Below this size a shape is not a room — it is the hole in a
                        printed 0, a hatching speck, a dimension tick.
                    </p>
                    <p>
                        These are not deleted. They are handed back to whatever
                        surrounds them, so a unit with its number printed inside it
                        keeps its full floor area instead of being left with the label
                        punched out of it.
                    </p>
                    <p>Raise it if tiny fragments are showing up in the shape list.</p>
                </Help>
            </label>

            <label className="pi-field">
                <span>Enclosure needed to read as indoors — {opts.minEnclosureRays} of 8</span>
                <input type="range" min="2" max="8" value={opts.minEnclosureRays}
                    onChange={(e) => set({ minEnclosureRays: Number(e.target.value) })} />
                <em>Lower if promenades are being proposed as outside the building.</em>
                <Help title="Enclosure">
                    <p>
                        How the importer tells the mall floor from the car park, without
                        being told where the building is.
                    </p>
                    <p>
                        Stand in the middle of a promenade and look around: shopfronts in
                        almost every direction. Do the same in the car park and you
                        mostly see the edge of the page. This counts how many of eight
                        directions hit a shop.
                    </p>
                    <p>
                        Lower it if promenades are coming back as "Outside the building".
                    </p>
                </Help>
            </label>

            <label className="pi-field">
                {/* Range starts at 2, not 50: the useful band turned out to be an
                    order of magnitude finer than the old default, and a slider
                    that cannot reach the right answer is worse than no slider. */}
                <span>Outline smoothing — {(opts.simplifyFraction * 10000).toFixed(0)}</span>
                <input type="range" min="2" max="60" value={Math.round(opts.simplifyFraction * 10000)}
                    onChange={(e) => set({ simplifyFraction: Number(e.target.value) / 10000 })} />
                <em>Higher gives fewer, straighter walls.</em>
            </label>

            <label className="pi-check">
                <input type="checkbox" checked={opts.squareUp}
                    onChange={(e) => set({ squareUp: e.target.checked })} />
                <span>Square up near-straight walls</span>
                <Help title="Square up walls">
                    <p>
                        Snaps walls that are within a few degrees of horizontal or
                        vertical so they come out exactly straight.
                    </p>
                    <p>
                        Off by default, because a mall plan is full of genuine angles —
                        radial arcades, curved promenades — and squaring those up
                        damages real geometry to tidy up a few walls.
                    </p>
                </Help>
            </label>
            </Advanced>
            )}

            {stats && (
                <>
                    <div className="pi-stats">
                        <div><span>Shapes traced</span><strong>{stats.kept}</strong></div>
                        <div><span>Specks absorbed</span><strong>{stats.rejectedSmall}</strong></div>
                        {stats.labelsDissolved > 0 && (
                            <div><span>Printed labels</span><strong>{stats.labelsDissolved}</strong></div>
                        )}
                    </div>
                    <ul className="pi-kind-tally">
                        {REGION_KINDS.filter((k) => stats.byKind[k.id]).map((k) => (
                            <li key={k.id}>
                                <span className="pi-swatch is-sm" style={{ background: k.swatch }} />
                                {k.label}
                                <strong>{stats.byKind[k.id]}</strong>
                            </li>
                        ))}
                    </ul>

                    {/*
                      * Said here, the moment the trace finishes, rather than only on
                      * the step where it is fixed. A trace that reports a tidy count
                      * of shapes and nothing else reads as a trace that worked, and
                      * the operator moves on. If some of it is doubtful they should
                      * hear it before they believe the number above.
                      */}
                    {doubtful > 0 && (
                        <button type="button" className="pi-trace-doubt" onClick={onReview}>
                            <ShieldAlert size={15} />
                            <span>
                                <strong>
                                    {doubtful} of those {doubtful === 1 ? 'is' : 'are'} worth
                                    {' '}checking.
                                </strong>
                                {' '}The drawing was ambiguous there — most often two units
                                traced as one. They are listed on the mapping step, and
                                ringed in red on the plan.
                            </span>
                            <ChevronRight size={14} />
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

function CalibrateStep({
    knownScale, drawingCal, setDrawingCal, calLine, calMetres, setCalMetres,
    planWidthM, planHeightM, onClear,
}) {
    return (
        <div className="pi-sect">
            <h4>3 · Set the scale</h4>

            {knownScale && !calLine && (
                <p className="pi-known">
                    <Check size={13} />
                    Scale read from the file ({knownScale.source}). Draw a line only if
                    it looks wrong — doing so overrides it.
                </p>
            )}
            <p className="pi-hint">
                Draw a line across something you know the length of — a unit frontage,
                a corridor width, the overall building — then type that length.
                Without this every area, rent-per-m² and walking time would be
                meaningless.
            </p>

            <button
                type="button"
                className={`mm-btn${drawingCal ? ' is-primary' : ''} pi-run`}
                onClick={() => setDrawingCal(true)}
            >
                <Ruler size={15} /> {calLine ? 'Redraw reference line' : 'Draw reference line'}
            </button>

            <label className="pi-field">
                <span>That line is</span>
                <div className="pi-inline">
                    <input
                        type="number"
                        min="1"
                        value={calMetres}
                        onChange={(e) => setCalMetres(Math.max(1, Number(e.target.value) || 1))}
                    />
                    <em>metres</em>
                </div>
            </label>

            {calLine && (
                <button type="button" className="mm-btn is-ghost" onClick={onClear}>
                    <RotateCcw size={13} /> Clear line
                </button>
            )}

            <div className="pi-stats">
                <div><span>Plan width</span><strong>{planWidthM ? `${formatNumber(planWidthM)} m` : '—'}</strong></div>
                <div><span>Plan depth</span><strong>{planHeightM ? `${formatNumber(planHeightM)} m` : '—'}</strong></div>
            </div>

            {!calLine && !knownScale && (
                <p className="pi-warn"><AlertTriangle size={13} /> No scale set — publishing is blocked.</p>
            )}
        </div>
    );
}

/**
 * The mapping step.
 *
 * Two mechanisms, in the order they save the most time: assign a category to a
 * whole fill colour at once, then run the naming loop shop by shop with Enter
 * carrying you to the next one.
 */
function MapStep({
    regions, filtered, palette, selected, nameInputRef, namedCount, categorisedCount,
    listFilter, setListFilter, query, setQuery, kindCounts,
    onSelect, onUpdate, onDelete, onColorCategory, onColorKind, onRegionKind,
    onDeleteColorGroup, onNext, planWidthM, planHeightM,
    drawShape, drawNote, onStartDraw, onCancelDraw, onFinishDraw,
    mergeIds, mergeNote, onStartMerge, onCancelMerge, onToggleMerge, onCommitMerge,
    onDismissFlags,
}) {
    // Shapes the trace is unsure about and nobody has looked at yet.
    const doubtful = regions.filter((r) => !r.reviewed && r.flags && r.flags.length);
    const serious = doubtful.filter((r) => r.flags.some((f) => f.severity === 'high'));
    const picked = mergeIds
        ? mergeIds.map((id) => regions.find((r) => r.id === id)).filter(Boolean)
        : [];
    const glaOf = (r) => {
        if (!planWidthM) return null;
        // Rough guide only; the published figure uses true polygon area.
        return Math.round(r.areaFraction * planWidthM * planHeightM);
    };

    // What a colour group currently resolves to, for the swatch row. An empty
    // string means the group is split, and the picker offers "Mixed".
    const groupValue = (hex, field) => {
        const inGroup = regions.filter((r) => r.colorKey === hex);
        if (!inGroup.length) return '';
        const first = inGroup[0][field];
        return inGroup.every((r) => r[field] === first) ? first : '';
    };

    const commitName = (value) => {
        if (!selected) return;
        const patch = { name: value };
        // Typing a brand we know is also a statement about its category, so
        // don't make the operator set it twice.
        const cat = BRAND_INDEX.get(value.trim());
        if (cat && !selected.manualCategory) {
            patch.category = cat;
            patch.manualCategory = true;
            patch.categorised = true;
        }
        onUpdate(selected.id, patch);
    };

    const groups = palette.filter((p) => regions.some((r) => r.colorKey === p.color));
    const unitTotal = regions.filter((r) => isLeasable(r.kind)).length;
    const pct = unitTotal ? Math.round((namedCount / unitTotal) * 100) : 0;

    return (
        <div className="pi-sect">
            <h4>
                4 · Map the plan <em>{regions.length}</em>
                <Help title="Mapping the plan">
                    <p>
                        Every shape the plan draws is listed here. Say what each one IS
                        — a shop, mall floor, an atrium void, back-of-house — and, for
                        shops, name it and set its category.
                    </p>
                    <p>
                        Start with <strong>Map by plan colour</strong> below. Your plan
                        already colour-codes itself, so answering once per colour
                        settles most of the floor in a few clicks.
                    </p>
                    <p>
                        The kind matters beyond the picture: it decides what counts as
                        leasable area, and what wayfinding is allowed to walk through.
                    </p>
                </Help>
            </h4>

            {/*
              * What the trace is unsure about, before anything else on the step.
              *
              * A trace of a real sheet is right about the great majority and wrong
              * about a handful, and the handful is invisible: a merged pair looks
              * exactly like a large unit, and a split one looks exactly like two
              * small ones. Checking two hundred shapes by eye to find four is work
              * nobody does, so without this the four ship.
              *
              * So the doubts are collected, ranked and put where they are the first
              * thing seen -- each one a button that takes you to the shape, and each
              * one dismissable, because a flag is a question and the operator is
              * allowed to answer "that one is fine".
              */}
            {!!doubtful.length && (
                <div className={`pi-review${serious.length ? ' is-serious' : ''}`}>
                    <div className="pi-review-head">
                        <ShieldAlert size={15} />
                        <strong>
                            {doubtful.length} shape{doubtful.length === 1 ? '' : 's'} worth checking
                        </strong>
                        <Help title="Shapes worth checking">
                            <p>
                                The trace reads the drawing, and a drawing does not always
                                say whether a line is a wall between two units or a line
                                inside one. These are the shapes where it could not tell.
                            </p>
                            <p>
                                <strong>Two unit numbers in one shape</strong> is the
                                strongest sign: the plan has printed two numbers inside
                                what was traced as a single unit, at opposite ends of it,
                                so it is almost certainly two units joined.
                            </p>
                            <p>
                                The others are proportions — a shape far bigger than its
                                neighbours, or a thin sliver next to normal units. Those
                                are guesses, and an anchor store or an odd corner will set
                                them off honestly.
                            </p>
                            <p>
                                Click one to see it on the plan. Fix it by joining shapes,
                                drawing the missing one, or deleting it — or dismiss it if
                                it is right as traced. Dismissing changes nothing about the
                                shape.
                            </p>
                        </Help>
                        <button
                            type="button"
                            className="pi-review-clear"
                            onClick={() => onDismissFlags(null)}
                        >
                            Dismiss all
                        </button>
                    </div>
                    <ul className="pi-review-list">
                        {doubtful.map((r) => {
                            const worst = r.flags.find((f) => f.severity === 'high') || r.flags[0];
                            return (
                                <li key={r.id} className={worst.severity === 'high' ? 'is-high' : ''}>
                                    <button
                                        type="button"
                                        className="pi-review-go"
                                        onClick={() => onSelect(r.id)}
                                    >
                                        <span className="pi-review-name">
                                            {r.code || r.name || kindOf(r.kind).label}
                                        </span>
                                        <em>{worst.note}</em>
                                    </button>
                                    <button
                                        type="button"
                                        className="pi-review-ok"
                                        title="This one is right — stop flagging it"
                                        onClick={() => onDismissFlags(r.id)}
                                    >
                                        <Check size={13} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/*
              * Tracing gets the great majority of a sheet and the stragglers are
              * always the awkward ones. Drawing the last few by hand is faster and
              * more predictable than pushing thresholds around until they appear,
              * and it cannot break the shapes that already came out right.
              */}
            <div className={`pi-draw-box${drawShape || mergeIds ? ' is-active' : ''}${mergeIds ? ' is-joining' : ''}`}>
                {mergeIds ? (
                    <div className="pi-draw-live is-merge">
                        <strong><Combine size={13} /> Joining shapes into one unit</strong>
                        <p>
                            Click each piece on the plan, or in the list below. Click a
                            picked one again to drop it. <kbd>Enter</kbd> joins them,
                            {' '}<kbd>Esc</kbd> cancels.
                        </p>
                        {!!picked.length && (
                            <ul className="pi-merge-picked">
                                {picked.map((r) => (
                                    <li key={r.id}>
                                        <span className="mm-cat-dot" style={{ background: regionColor(r) }} />
                                        {r.code || r.name || kindOf(r.kind).label}
                                        {glaOf(r) ? <em>{formatNumber(glaOf(r))} m²</em> : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="pi-draw-actions">
                            <span className="pi-draw-count">
                                {picked.length} picked
                                {picked.length >= 2 && glaOf(picked[0]) ? ` · ${formatNumber(
                                    picked.reduce((n, r) => n + (glaOf(r) || 0), 0),
                                )} m² together` : ''}
                            </span>
                            <button
                                type="button"
                                className="mm-btn is-primary"
                                onClick={onCommitMerge}
                                disabled={picked.length < 2}
                            >
                                <Combine size={13} /> Join {picked.length >= 2 ? picked.length : ''}
                            </button>
                            <button type="button" className="mm-btn is-ghost" onClick={onCancelMerge}>
                                <X size={13} /> Cancel
                            </button>
                        </div>
                        {mergeNote && mergeNote.error && (
                            <p className="pi-merge-error"><AlertTriangle size={13} /> {mergeNote.error}</p>
                        )}
                    </div>
                ) : !drawShape ? (
                    <>
                        <button type="button" className="mm-btn is-ghost pi-draw-start" onClick={onStartDraw}>
                            <PenLine size={14} /> Add a missing shape
                        </button>
                        <Help title="Adding a shape by hand">
                            <p>
                                For the few units the trace misses — usually one whose
                                printed number runs across its own wall, or one the
                                drawing leaves open at a corner.
                            </p>
                            <p>
                                Click each corner on the plan, then click the first
                                corner again to close it. It picks up the plan's colour
                                underneath, so it joins the right colour group and gets
                                categorised with its neighbours.
                            </p>
                            <p>
                                What you draw <em>wins</em>. A traced shape mostly inside
                                it is replaced, and one merely clipped by it is trimmed to
                                fit around it — so its area is never counted twice and
                                nothing is left overlapping your shape in the model.
                            </p>
                        </Help>
                        <button type="button" className="mm-btn is-ghost pi-merge-start" onClick={onStartMerge}>
                            <Combine size={14} /> Join shapes
                        </button>
                        <Help title="Joining shapes">
                            <p>
                                For a unit the plan draws with a line through it — an old
                                demising wall, a shopfront set-back, a mezzanine edge. The
                                trace reads that line correctly and gets two shapes; only
                                you know it is one tenancy.
                            </p>
                            <p>
                                Pick the pieces and join them. The outline of the result is
                                the outside of what you picked, the seam between them goes,
                                and the floor area is the sum — it is one unit, not two
                                overlapping.
                            </p>
                            <p>
                                The largest piece keeps its unit code and category. Shapes
                                that do not touch are refused rather than joined with a gap
                                down the middle.
                            </p>
                        </Help>
                    </>
                ) : (
                    <div className="pi-draw-live">
                        <strong><PenLine size={13} /> Drawing a shape</strong>
                        <p>
                            Click each corner on the plan. Click the first corner again,
                            or press <kbd>Enter</kbd>, to finish.
                            {' '}<kbd>Backspace</kbd> takes back a corner,
                            {' '}<kbd>Esc</kbd> cancels.
                        </p>
                        <div className="pi-draw-actions">
                            <span className="pi-draw-count">
                                {drawShape.length} corner{drawShape.length === 1 ? '' : 's'}
                            </span>
                            <button
                                type="button"
                                className="mm-btn is-primary"
                                onClick={onFinishDraw}
                                disabled={drawShape.length < 3}
                            >
                                <Check size={13} /> Finish
                            </button>
                            <button type="button" className="mm-btn is-ghost" onClick={onCancelDraw}>
                                <X size={13} /> Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {mergeNote && !mergeNote.error && !mergeIds && (
                <p className="pi-draw-note">
                    <Check size={13} />
                    <span>
                        {mergeNote.joined} shapes joined into {mergeNote.code}. Its area is
                        now the whole of what you picked, counted once.
                    </span>
                </p>
            )}

            {drawNote && !drawShape && (
                <p className="pi-draw-note">
                    <Check size={13} />
                    <span>
                        Your shape took the ground it covers
                        {drawNote.replaced > 0 && ` — ${drawNote.replaced} traced `}
                        {drawNote.replaced > 0 && (drawNote.replaced === 1 ? 'shape replaced' : 'shapes replaced')}
                        {drawNote.replaced > 0 && drawNote.trimmed > 0 && ','}
                        {drawNote.trimmed > 0 && ` ${drawNote.trimmed} trimmed to fit around it`}
                        .
                    </span>
                </p>
            )}

            <div className="pi-progress">
                <div className="pi-progress-bar"><span style={{ width: `${pct}%` }} /></div>
                <div className="pi-progress-meta">
                    <span>{namedCount} of {unitTotal} units named</span>
                    <span>{categorisedCount} categorised</span>
                </div>
            </div>

            <ul className="pi-kind-tally">
                {REGION_KINDS.filter((k) => kindCounts[k.id]).map((k) => (
                    <li key={k.id}>
                        <span className="pi-swatch is-sm" style={{ background: k.swatch }} />
                        {k.label}
                        <strong>{kindCounts[k.id]}</strong>
                    </li>
                ))}
            </ul>

            {!!groups.length && (
                <div className="pi-group-box">
                    <h5>
                        <Wand2 size={13} /> Map by plan colour
                        <Help title="Map by plan colour">
                            <p>
                                Each row is one fill colour found on the drawing, and the
                                count is how many shapes wear it.
                            </p>
                            <p>
                                The first dropdown says what that colour IS. The second,
                                for retail colours, sets the category for all of them at
                                once.
                            </p>
                            <p>
                                Anything you have already set on an individual shape is
                                left alone, so bulk changes never undo your corrections.
                            </p>
                        </Help>
                    </h5>
                    <p className="pi-hint">
                        Your plan already colour-codes itself. Say what each colour IS
                        once — and, for retail, which category — and every shape wearing
                        it follows. Two clicks per colour instead of two per shape.
                    </p>
                    <ul className="pi-groups">
                        {groups.map((g) => {
                            const kind = groupValue(g.color, 'kind');
                            return (
                                <li key={g.color}>
                                    <span className="pi-swatch" style={{ background: g.color }} />
                                    <span className="pi-group-count">{g.count}</span>
                                    <select
                                        value={kind}
                                        onChange={(e) => onColorKind(g.color, e.target.value)}
                                    >
                                        {!kind && <option value="">Mixed</option>}
                                        {KIND_PICKER_ORDER.map((id) => (
                                            <option key={id} value={id}>{kindOf(id).label}</option>
                                        ))}
                                    </select>
                                    {isLeasable(kind) ? (
                                        <select
                                            value={groupValue(g.color, 'category')}
                                            onChange={(e) => onColorCategory(g.color, e.target.value)}
                                        >
                                            {!groupValue(g.color, 'category') && <option value="">Mixed</option>}
                                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    ) : <span />}
                                    <button
                                        type="button"
                                        className="pi-group-del"
                                        title="Delete every shape of this colour"
                                        onClick={() => onDeleteColorGroup(g.color)}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {selected ? (
                <div className="pi-editor is-sticky">
                    <div className="pi-editor-head">
                        <span className="mm-cat-dot" style={{ background: regionColor(selected) }} />
                        <strong>{selected.code || kindOf(selected.kind).label}</strong>
                        <em>{glaOf(selected) ? `${formatNumber(glaOf(selected))} m²` : 'set scale'} · {selected.polygon.length} corners</em>
                    </div>

                    <div className="pi-field">
                        <span>This shape is</span>
                        <div className="pi-type-grid is-kinds">
                            {KIND_PICKER_ORDER.map((id) => (
                                <button
                                    key={id}
                                    type="button"
                                    title={kindOf(id).hint}
                                    className={selected.kind === id ? 'is-active' : ''}
                                    onClick={() => onRegionKind(selected.id, id)}
                                >
                                    <span className="pi-swatch is-sm" style={{ background: kindOf(id).swatch }} />
                                    {kindOf(id).label}
                                </button>
                            ))}
                        </div>
                        <em>{kindOf(selected.kind).hint}</em>
                    </div>

                    {isLeasable(selected.kind) && (
                    <label className="pi-field">
                        <span>Trading name</span>
                        <input
                            ref={nameInputRef}
                            list="pi-brands"
                            value={selected.name}
                            placeholder="Start typing a brand…"
                            onChange={(e) => commitName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); onNext(); }
                                if (e.key === 'Escape') e.currentTarget.blur();
                            }}
                        />
                        <em><CornerDownLeft size={11} /> Enter saves and jumps to the next unnamed unit.</em>
                    </label>
                    )}

                    {isLeasable(selected.kind) && (
                    <div className="pi-editor-row">
                        <label className="pi-field">
                            <span>Category</span>
                            <select
                                value={selected.category}
                                onChange={(e) => onUpdate(selected.id, {
                                    category: e.target.value, manualCategory: true, categorised: true,
                                })}
                            >
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="pi-field">
                            <span>Status</span>
                            <select value={selected.status} onChange={(e) => onUpdate(selected.id, { status: e.target.value })}>
                                {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                    </div>
                    )}

                    {isLeasable(selected.kind) && (
                    <label className="pi-field">
                        <span>Unit code</span>
                        <input value={selected.code} onChange={(e) => onUpdate(selected.id, { code: e.target.value })} />
                    </label>
                    )}

                    <div className="pi-editor-actions">
                        <button type="button" className="mm-btn is-primary" onClick={onNext}>
                            Next unnamed <ChevronRight size={14} />
                        </button>
                        <button type="button" className="mm-btn is-ghost" onClick={() => onDelete(selected.id)}>
                            <Trash2 size={13} /> Delete shape
                        </button>
                    </div>
                </div>
            ) : (
                <p className="pi-hint">
                    Click a shape on the plan — or a row below — to say what it is.
                    Only shapes marked as a retail unit get a lease and sales; the rest
                    are modelled so the floor reads as a building.
                </p>
            )}

            <datalist id="pi-brands">
                {BRAND_NAMES.map((b) => <option key={b} value={b} />)}
            </datalist>

            <div className="pi-list-tools">
                <div className="pi-search">
                    <Search size={13} />
                    <input value={query} placeholder="Filter…" onChange={(e) => setQuery(e.target.value)} />
                </div>
                <div className="pi-chips">
                    {['all', 'units', 'unnamed', 'other'].map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={listFilter === f ? 'is-active' : ''}
                            onClick={() => setListFilter(f)}
                        >
                            {f}
                        </button>
                    ))}
                    {!!doubtful.length && (
                        <button
                            type="button"
                            className={`is-doubt${listFilter === 'to check' ? ' is-active' : ''}`}
                            onClick={() => setListFilter(listFilter === 'to check' ? 'all' : 'to check')}
                        >
                            to check <strong>{doubtful.length}</strong>
                        </button>
                    )}
                </div>
            </div>

            <ul className="pi-list">
                {filtered.map((r) => {
                    const inMerge = !!mergeIds && mergeIds.includes(r.id);
                    const doubt = !r.reviewed && r.flags && r.flags.length
                        ? (r.flags.some((f) => f.severity === 'high') ? ' is-high' : ' is-low')
                        : '';
                    return (
                        <li key={r.id}>
                            {/* While joining, the list is a second way to pick pieces —
                                easier than hitting a two-metre sliver on the plan. */}
                            <button
                                type="button"
                                className={`${r.id === (selected && selected.id) ? 'is-active' : ''}`
                                    + `${inMerge ? ' is-picked' : ''}${doubt}`}
                                onClick={() => (mergeIds ? onToggleMerge(r.id) : onSelect(r.id))}
                            >
                                <span className="mm-cat-dot" style={{ background: regionColor(r) }} />
                                <span className="pi-list-name">
                                    {r.name || r.code || kindOf(r.kind).label}
                                </span>
                                {!!doubt && <ShieldAlert size={12} className="pi-list-flag" />}
                                <em style={{ color: isLeasable(r.kind) ? STATUS_COLORS[r.status] : undefined }}>
                                    {isLeasable(r.kind) ? r.category : kindOf(r.kind).label}
                                </em>
                            </button>
                        </li>
                    );
                })}
                {!filtered.length && <li className="pi-list-empty">Nothing matches that filter.</li>}
            </ul>
        </div>
    );
}

/**
 * Amenities.
 *
 * The detector proposes markers where the drawing PROVES something is there —
 * a boxed pictogram, or the regular hatching of a stair or escalator run. It
 * does not guess which pictogram: that needs a trained classifier, and a wrong
 * guess would put a lift on a floor that has none and route people through it.
 * So typing a marker is one click, and anything missed can be dropped on by
 * hand. Untyped markers are never published.
 */
function AmenityStep({ markers, setMarkers, selectedMarker, setSelectedMarker, placing, setPlacing, walkable, circulation }) {
    const sel = markers.find((m) => m.id === selectedMarker) || null;
    const typed = markers.filter((m) => m.type).length;

    const update = (id, patch) => setMarkers((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    const remove = (id) => {
        setMarkers((ms) => ms.filter((m) => m.id !== id));
        if (selectedMarker === id) setSelectedMarker(null);
    };

    return (
        <div className="pi-sect">
            <h4>5 · Amenities <em>{markers.length}</em></h4>
            <p className="pi-hint">
                Lifts, escalators, stairs and restrooms. Detected markers are the
                boxed pictograms and hatched stair runs found on the sheet. Hatching
                is taken as vertical circulation because the drawing says so; the
                rest are untyped until you say what they are.
            </p>

            <div className="pi-stats">
                <div><span>Markers</span><strong>{markers.length}</strong></div>
                <div><span>Typed</span><strong>{typed}</strong></div>
                <div><span>Walkable cells</span><strong>{walkable ? formatNumber(walkable.filled) : '0'}</strong></div>
                <div><span>Circulation areas</span><strong>{circulation.length}</strong></div>
            </div>

            {!walkable && (
                <p className="pi-warn">
                    <AlertTriangle size={13} />
                    No walkable space was traced, so routes cannot be computed. Go back
                    to Detect and check that the malls came back as circulation.
                </p>
            )}

            <button
                type="button"
                className={`mm-btn${placing ? ' is-primary' : ''} pi-run`}
                onClick={() => setPlacing(!placing)}
            >
                <Plus size={15} /> {placing ? 'Click the plan to place…' : 'Add a marker by hand'}
            </button>

            {sel ? (
                <div className="pi-editor is-sticky">
                    <div className="pi-editor-head">
                        <MapPin size={13} />
                        <strong>{sel.name || sel.type || 'Untyped marker'}</strong>
                        <em>{sel.confidence}</em>
                    </div>

                    <div className="pi-type-grid">
                        {AMENITY_TYPES.map((t) => (
                            <button
                                key={t}
                                type="button"
                                className={sel.type === t ? 'is-active' : ''}
                                onClick={() => update(sel.id, { type: sel.type === t ? null : t })}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <label className="pi-field">
                        <span>Name (optional)</span>
                        <input
                            value={sel.name}
                            placeholder={sel.type || 'e.g. North Court Lifts'}
                            onChange={(e) => update(sel.id, { name: e.target.value })}
                        />
                    </label>

                    <button type="button" className="mm-btn is-ghost" onClick={() => remove(sel.id)}>
                        <Trash2 size={13} /> Remove marker
                    </button>
                </div>
            ) : (
                <p className="pi-hint">Click a marker on the plan to type it.</p>
            )}

            <ul className="pi-list">
                {markers.map((m, i) => (
                    <li key={m.id}>
                        <button
                            type="button"
                            className={m.id === selectedMarker ? 'is-active' : ''}
                            onClick={() => setSelectedMarker(m.id)}
                        >
                            <span className="mm-cat-dot" style={{ background: m.type ? '#38BDF8' : '#64748B' }} />
                            <span className="pi-list-name">{m.name || m.type || `Marker ${i + 1}`}</span>
                            <em>{m.type ? m.confidence : 'untyped'}</em>
                        </button>
                    </li>
                ))}
                {!markers.length && <li className="pi-list-empty">No markers found — add them by hand.</li>}
            </ul>
        </div>
    );
}

function PublishStep({
    regions, markers, walkable, planWidthM, planHeightM, floorId, namedCount,
    unitCount, surfaceCount, onPublish,
}) {
    const blocked = !planWidthM || !regions.length;
    const floor = FLOORS.find((f) => f.id === floorId);
    const cores = markers.filter((m) => m.type === 'Lift' || m.type === 'Escalator' || m.type === 'Stairs').length;

    return (
        <div className="pi-sect">
            <h4>6 · Publish to the 3D map</h4>

            <div className="pi-stats">
                <div><span>Floor</span><strong>{floor ? floor.name : floorId}</strong></div>
                <div><span>Leasable units</span><strong>{unitCount}</strong></div>
                <div><span>Other surfaces</span><strong>{surfaceCount}</strong></div>
                <div><span>Named</span><strong>{namedCount} of {unitCount}</strong></div>
                <div><span>Amenities</span><strong>{markers.length}</strong></div>
                <div><span>Vertical cores</span><strong>{cores}</strong></div>
                <div><span>Plan size</span><strong>{planWidthM ? `${formatNumber(planWidthM)} × ${formatNumber(planHeightM)} m` : '—'}</strong></div>
            </div>

            <p className="pi-hint">
                Everything you marked as mall, atrium, core or back-of-house is
                published as geometry alongside the units, so the floor plate reads
                as a building rather than as shops on an empty slab. Only the units
                carry a lease.
            </p>

            <p className="pi-hint">
                <Route size={13} /> Wayfinding routes along whatever you marked walkable,
                so directions work on this plan the same way they do on the sample
                estate. Import another level and lifts drawn in the same place link
                the two.
            </p>

            <p className="pi-hint">
                Lease and trading figures are generated from the same demonstration
                model the sample estate uses — the geometry is yours, the commercials
                are not. Wire them to the API before treating any of it as real.
            </p>

            {!walkable && (
                <p className="pi-warn">
                    <AlertTriangle size={13} /> No walkable space traced — the map will
                    publish, but directions will have nothing to route along.
                </p>
            )}

            {blocked && (
                <p className="pi-warn">
                    <AlertTriangle size={13} />
                    {!regions.length ? 'Trace the plan first.' : 'Set the scale first.'}
                </p>
            )}

            <button type="button" className="mm-btn is-primary pi-run" disabled={blocked} onClick={onPublish}>
                <Rocket size={15} /> Publish and open in 3D
            </button>
        </div>
    );
}
